import React, { useState } from "react";
import { DocumentText, Import, Receipt21 } from "iconsax-reactjs";
import { toast } from "sonner";
import { Button } from "../../../../shared/ui";
import { TransactionAttachment, TransactionItem } from "../../types";
import { TransactionDocumentPreviewModal } from "./TransactionDocumentPreviewModal";
import { AttachmentsEmptyState } from "../../../otc-requests/components/AttachmentsEmptyState";
import { useNavigate } from "react-router-dom";
import { downloadTransactionReceipt } from "../../services/downloadTransactionReceipt";

interface TransactionAttachmentsCardProps {
  transaction?: TransactionItem;
  attachments?: TransactionAttachment[];
  onDownloadAttachment?: (attachment: TransactionAttachment) => void;
  onPreviewAttachment?: (attachment: TransactionAttachment) => void;
}

interface ResolvedMediaItem {
  id: string;
  title: string;
  type: "invoice" | "supporting_doc" | "receipt" | "proof";
  url?: string;
  isAvailable: boolean;
  fileName?: string;
}

export const TransactionAttachmentsCard: React.FC<
  TransactionAttachmentsCardProps
> = ({
  transaction,
  attachments,
  onDownloadAttachment,
  onPreviewAttachment,
}) => {
  const navigate = useNavigate();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [activeMedia, setActiveMedia] = useState<ResolvedMediaItem | null>(null);

  // Derive media items dynamically from live transaction data
  const mediaItems: ResolvedMediaItem[] = React.useMemo(() => {
    if (attachments && attachments.length > 0) {
      return attachments.map((att) => ({
        id: att.id,
        title: att.title,
        type: att.type,
        url: att.fileUrl,
        isAvailable: Boolean(att.fileUrl && att.fileUrl !== "N/A"),
        fileName: att.fileName,
      }));
    }

    const items: ResolvedMediaItem[] = [];

    const isOtc = transaction?.TransactionType === "OTC";
    const isCompleted =
      transaction?.status?.toLowerCase() === "completed" ||
      transaction?.status?.toLowerCase() === "successful" ||
      transaction?.status?.toLowerCase() === "success";

    // 1. Customer Receipt
    const receiptDocUrl =
      typeof transaction?.url === "object" ? transaction?.url?.url : transaction?.receiptUrl;

    if (!isOtc || receiptDocUrl || isCompleted) {
      items.push({
        id: "media-receipt",
        title: "Customer Receipt",
        type: "receipt",
        url: receiptDocUrl,
        isAvailable: Boolean(receiptDocUrl) || !isOtc,
        fileName: `Receipt-${transaction?.TransactionReference || transaction?.RequestReference || transaction?._id || "receipt"}.pdf`,
      });
    }

    // 2. Invoice (only rendered if present in real transaction data)
    const rawInvoice = transaction?.invoice || transaction?.invoiceDocument;
    const invoiceUrl =
      typeof rawInvoice === "string" ? rawInvoice : rawInvoice?.url;
    if (invoiceUrl && invoiceUrl !== "N/A") {
      items.push({
        id: "media-invoice",
        title: "Invoice",
        type: "invoice",
        url: invoiceUrl,
        isAvailable: true,
        fileName: `Invoice-${transaction?.TransactionReference || transaction?._id || "invoice"}.pdf`,
      });
    }

    // 3. Supporting document (only rendered if present in real transaction data)
    const rawSupport = transaction?.supportDocument || transaction?.proof;
    const supportUrl =
      typeof rawSupport === "string" ? rawSupport : rawSupport?.url;
    if (supportUrl && supportUrl !== "N/A") {
      items.push({
        id: "media-support",
        title: "Supporting document",
        type: "supporting_doc",
        url: supportUrl,
        isAvailable: true,
        fileName: `Supporting-Document-${transaction?.TransactionReference || transaction?._id || "support-doc"}.pdf`,
      });
    }

    return items;
  }, [attachments, transaction]);

  const handlePreview = (item: ResolvedMediaItem) => {
    if (onPreviewAttachment) {
      onPreviewAttachment({
        id: item.id,
        title: item.title,
        type: item.type,
        fileUrl: item.url,
        fileName: item.fileName,
      });
      return;
    }

    setActiveMedia(item);
    setPreviewOpen(true);
  };

  const handleDownload = (e: React.MouseEvent, item: ResolvedMediaItem) => {
    e.stopPropagation();

    if (onDownloadAttachment) {
      onDownloadAttachment({
        id: item.id,
        title: item.title,
        type: item.type,
        fileUrl: item.url,
        fileName: item.fileName,
      });
      return;
    }

    if (item.url && item.url !== "N/A") {
      const link = document.createElement("a");
      link.href = item.url;
      link.download = item.fileName || `${item.title.toLowerCase().replace(/\s+/g, "-")}.pdf`;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`Downloading ${item.title}...`);
    } else if (item.type === "receipt") {
      const targetTrxId = transaction?._id || transaction?.id;
      if (targetTrxId) {
        downloadTransactionReceipt(targetTrxId);
      } else if (transaction) {
        navigate("/receipt/home", { state: transaction });
      } else {
        setActiveMedia(item);
        setPreviewOpen(true);
      }
    } else {
      setActiveMedia(item);
      setPreviewOpen(true);
    }
  };

  return (
    <>
      <div className="flex h-full min-h-[340px] flex-col justify-start rounded-2xl border border-[#EEF1FB] bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-6">
          {mediaItems.length === 0 && (
            <div className="flex flex-1 items-center justify-center py-6">
              <AttachmentsEmptyState />
            </div>
          )}

          {mediaItems.map((item) => (
            <div
              key={item.id}
              onClick={() => handlePreview(item)}
              className="group flex cursor-pointer items-center justify-between transition-opacity hover:opacity-90"
              title={`Click to preview ${item.title}`}
            >
              <div className="flex items-center gap-3.5">
                <div className="shrink-0">
                  {item.type === "supporting_doc" || item.type === "proof" ? (
                    <DocumentText size={22} variant="Bold" color="#7F56D9" />
                  ) : (
                    <Receipt21 size={22} variant="Bold" color="#7F56D9" />
                  )}
                </div>
                <span className="text-[14px] font-medium text-[#101828]">
                  {item.title}
                </span>
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={(e) => handleDownload(e, item)}
                className="h-auto border-[#D0D5DD] bg-white px-4 py-2 text-[14px] font-medium text-[#344054] shadow-xs hover:bg-[#F9FAFB] hover:text-[#101828]"
                title={`Download ${item.title}`}
              >
                <Import size={18} variant="Outline" color="#344054" />
                <span>Download</span>
              </Button>
            </div>
          ))}
        </div>
      </div>

      <TransactionDocumentPreviewModal
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        title={activeMedia?.title || "Document"}
        documentUrl={activeMedia?.url}
        documentType={activeMedia?.type}
        transaction={transaction}
      />
    </>
  );
};
